import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TASK } from "@/constants/testIds";

export default function ChatPanel({ messages, onSent, taskId, wsRef, currentUser }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    const payload = { type: "message", text: text.trim() };
    const ws = wsRef?.current;
    if (ws && ws.readyState === 1) {
      try {
        ws.send(JSON.stringify(payload));
        setText("");
        setBusy(false);
        return;
      } catch (err) {
        // WS send failed — fall through to REST fallback
        console.warn("ChatPanel: WS send failed, falling back to REST", err);
      }
    }
    try {
      const { data } = await api.post(`/tasks/${taskId}/messages`, { text: text.trim() });
      onSent(data);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="eb-card p-5 lg:sticky lg:top-24 flex flex-col h-[640px]">
      <div className="flex items-center justify-between pb-3 border-b border-[var(--eb-border)]">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-[var(--eb-primary)]" />
          <h3 className="font-[800]">Live chat</h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-success)]">● live</span>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto eb-scrollbar pt-3 space-y-3 pr-1">
        {messages.length === 0 && (
          <div className="text-[var(--eb-text-dim)] text-sm text-center py-10">
            Start the conversation. WhatsApp-style, but on the record.
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === currentUser?.user_id;
          if (m.kind === "system") {
            return (
              <div
                data-testid={TASK.chatMessage}
                data-kind="system"
                key={m.message_id}
                className="mx-auto text-center"
              >
                <div className="inline-block px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.12em] bg-amber-100 text-amber-800 border border-amber-200">
                  {m.text}
                </div>
                <div className="text-[10px] mt-1 text-[var(--eb-text-faint)]">
                  {m.sender_name} · {new Date(m.created_at).toLocaleTimeString()}
                </div>
              </div>
            );
          }
          return (
            <div data-testid={TASK.chatMessage} key={m.message_id} className={`max-w-[85%] ${mine ? "ml-auto" : ""}`}>
              <div
                className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  mine
                    ? "bg-[var(--eb-primary)] text-white rounded-br-sm"
                    : "bg-[var(--eb-surface-2)] text-[var(--eb-text)] rounded-bl-sm"
                }`}
              >
                {m.text}
              </div>
              <div className={`text-[10px] mt-1 text-[var(--eb-text-faint)] ${mine ? "text-right" : ""}`}>
                {m.sender_name} · {new Date(m.created_at).toLocaleTimeString()}
              </div>
            </div>
          );
        })}
      </div>
      <div className="pt-3 border-t border-[var(--eb-border)] flex gap-2">
        <input
          data-testid={TASK.chatInput}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          className="eb-input"
          placeholder="Type a message…"
        />
        <button
          data-testid={TASK.chatSend}
          onClick={send}
          disabled={busy}
          className="eb-btn eb-btn-primary !py-2 !px-3"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
